import { Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { AmountRow } from '~/commonComponents';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import type { IJob } from '~/models';

export interface ICancelScheduledShiftPopupProps {
  job: IJob;
  /** Dismisses without cancelling. */
  onClose: () => void;
  /** Confirms the cancellation. The caller owns the mutation and the redirect. */
  onConfirm: () => void;
  /** Whether the cancellation is in flight; shows the spinner on the confirm button. */
  isPending?: boolean;
}

/** Legacy copy, verbatim — it is the office's cancellation policy, so it must not be reworded. */
const SETTLEMENT_NOTE = 'Please allow 5-7 business days for funds to post to your account.';

const POLICY_NOTE =
  'For more details about our Cancellation Policy, please refer to our FAQs page.';

/**
 * Width of the money block, from the legacy popup's own `width: 400`.
 *
 * The modal itself is fluid (`30%`, floor 450, ceiling 700), so without this the
 * rows stretch to whatever the viewport gives and the label/amount pair ends up
 * separated by a lake of whitespace. Centred, so it stays under the title.
 */
const BREAKDOWN_WIDTH = '400px';

/**
 * `ConfirmPopupContent` puts a hardcoded `marginBottom: 70` under the
 * description, sized for a popup whose description is the last thing before the
 * buttons. This one has a whole receipt after it, so the default leaves a gap the
 * size of the block it is meant to introduce. `width` goes to 100% for the same
 * reason: the default 80% makes the sentence visibly narrower than the rows below it.
 */
const DESCRIPTION_STYLE = { marginBottom: 24, width: '100%' } as const;

/**
 * The confirmation the office sees before cancelling a shift somebody is already
 * booked for.
 *
 * Deliberately **not** the OPEN page's plain `ConfirmPopup`: cancelling a filled
 * shift costs money. The legacy `CancelledJob` discloses the charge, the
 * cancellation fee and the resulting refund before the office commits, and
 * dropping that would let them cancel a paid booking without seeing what it
 * costs. Every string, every row and the fee row's red label are that popup's.
 *
 * What is *not* reused is `CancelledJob` itself, and the reason is load-bearing:
 * it reads its shift from `state.job.job` in redux, which only `JobDetails.js`
 * populates (via its `fetchJob` dispatch). The redesign's gate loads the shift
 * through React Query instead, so on this page that redux slot is either empty —
 * and `CancelledJob` dereferences `job.total` unguarded, so it throws — or, worse,
 * still holding whichever shift was opened last, and the office would be shown
 * another booking's refund. The popup is rebuilt against the shift this page
 * already has rather than teaching it a second data source.
 */
export const CancelScheduledShiftPopup = ({
  job,
  onClose,
  onConfirm,
  isPending = false,
}: ICancelScheduledShiftPopupProps) => {
  const subject = job.isOnsiteInterview ? 'Onsite Interview' : 'Shift';
  const noun = job.isOnsiteInterview ? 'onsite interview' : 'shift';

  return (
    <ConfirmPopup
      title={`Cancel ${subject}`}
      description={
        <>
          Are you sure you want to cancel this {noun} on <strong>{job.local_date}</strong>? Please
          note: This {noun} will not be available to the community anymore.
        </>
      }
      descriptionStyle={DESCRIPTION_STYLE}
      leftButtonText="Go Back"
      leftButtonAction={onClose}
      leftButtonDataCy="cancel-scheduled-go-back"
      rightButtonText={job.isOnsiteInterview ? 'Cancel Interview' : 'Cancel Shift'}
      rightButtonDataCy="confirm-cancel-scheduled"
      rightButtonAction={onConfirm}
      rightButtonLoading={isPending}
      closePopup={onClose}
      dataTestId="cancel-scheduled-shift-popup"
    >
      <Column
        width={BREAKDOWN_WIDTH}
        className="mx-auto max-w-full"
        data-testid="cancel-scheduled-breakdown"
      >
        <AmountRow label={`${subject} Charge:`} amount={job.total} divider />
        {/*
         * The legacy hides the fee row when it is exactly zero rather than
         * printing "-$0.00", which reads as a charge the office has to reason
         * about. A missing fee is the same case. The label is red because a fee is
         * the one line here that costs the office money.
         */}
        {job.cancellation_fee ? (
          <AmountRow
            label={
              <Typography as="span" bold color={colors.error[500]}>
                Cancellation Fee:
              </Typography>
            }
            amount={job.cancellation_fee}
            variant="credit"
            divider
          />
        ) : null}
        {/*
         * Not `variant="total"`: that colours the label green, which the legacy
         * reserves for money the office *earns*. A refund total is bold, not green.
         */}
        <AmountRow
          label={
            <Typography as="span" bold>
              Total Refund:
            </Typography>
          }
          amount={job.total_refund}
          divider
        />

        <Column gap={2} pt={4} pb={4}>
          <Typography variant="body2" as="p" textAlign="center" color={colors.neutral[500]}>
            {SETTLEMENT_NOTE}
          </Typography>
          <Typography variant="body2" as="p" textAlign="center" color={colors.neutral[500]}>
            {POLICY_NOTE}
          </Typography>
        </Column>
      </Column>
    </ConfirmPopup>
  );
};
